import { useCallback, useEffect, useState } from 'react';
import type { MessageType } from '@lingui/conf';
import { type I18n, i18n as i18nCore } from '@lingui/core';

const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';
const DEV_PATH = `/i18n/languages`;

type Messages = Record<string, MessageType>;

export function useLoadLocale(
  language: string | undefined | null,
  type: 'configurator' | 'widget',
  i18n?: I18n
) {
  const instance = i18n || i18nCore;
  const [messages, setMessages] = useState<Messages | undefined>();

  const loadAndActivate = useCallback(
    async (language: string, destination: string) => {
      const messages = await fetchMessages(destination);
      setMessages({ ...messages });

      instance.load({ [language]: messages });
      instance.activate(language);
    },
    // oxlint-disable-next-line exhaustive-deps
    []
  );

  useEffect(() => {
    if (language) {
      loadAndActivate(
        language,
        resolveDictionaryURL(import.meta.env.BASE_URL, language, type)
      );
    }
  }, [language, type, loadAndActivate]);

  return messages;
}

async function fetchMessages(path: string) {
  return fetch(path)
    .then((response) => response.json())
    .then((messages) => messages)
    .catch((e) => {
      console.warn(`Can't load messages for locale`);
      console.error(e);
    });
}

function resolveDictionaryURL(
  baseURL: string,
  language: string,
  type: 'configurator' | 'widget'
) {
  // replace '/preview/' to the '/widget/' because we can launch the widget from the preview
  const normalizedBaseURLPath = baseURL.includes('/preview')
    ? baseURL.replace('/preview', '/widget')
    : baseURL;

  return IS_DEVELOPMENT
    ? `${DEV_PATH}/${type}/${language}.json`
    : `${normalizedBaseURLPath}/languages/${language}.json`;
}
